import { useState, useEffect, useCallback, useRef } from 'react';
import { ONBOARDING_CLOSED_EVENT } from '@/constants/events';
import { useProfileNavGuard } from '@/context/ProfileNavGuardContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/atoms/Spinner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/molecules/Dialog';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { IconButton } from '@/components/atoms/IconButton';
import { Switch } from '@/components/atoms/Switch';
import { Select } from '@/components/atoms/Select';
import { Bell, BookOpen, Eye, Zap, Globe, Info, X, AlertTriangle } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@/components/molecules/Drawer';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ReactNode } from 'react';
import { Chip, ChipGroup } from '@/components/atoms/Chip';
import {
  STUDY_TIME_BANDS,
  STUDY_TIME_HOURS,
  type StudyTimeBand,
} from '@/components/molecules/StudyTimeSelector';
import { WEEKLY_ROUTINE_OPTIONS } from '@/components/molecules/WeeklyRoutineSelector';
import { StudyDaysSelector } from '@/components/molecules/StudyDaysSelector';
import { categoriesAPI } from '@/services/api/categories.api';
import { localizedField } from '@/utils/localizedField';
import { ROUTES } from '@/constants/routes';
import { useAsyncData } from '@/hooks/useAsyncData';
import type { Category } from '@/types/category.types';
import type { LearningPreferences } from '@/types/preferences.types';
import { studentI18n, setStudentDocumentLang } from '@/i18n/studentI18n';
import { setStoredStudentLocale, getStoredStudentLocale, type StudentLocale } from '@/constants/studentLocale';
import { useAuth } from '@/hooks/useAuth';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useToast } from '@/context/ToastContext';
import { getWeeklyGoal } from '@/services/api/goals.api';
import { getNotificationPreferences } from '@/services/api/notifications.api';
import { ApiClient } from '@/services/api/client';
import styles from './SettingsPage.module.scss';

const DEFAULT_NOTIFICATIONS = {
  emailNotifications: true,
  pushNotifications: true,
  storyReminders: true,
  achievementAlerts: true,
  streakReminders: true,
  dailyGoalReminders: true,
};

// Sin días guardados (usuarios previos a la feature) el recordatorio es todos
// los días; por eso el default refleja la semana completa.
const ALL_WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6];

const DEFAULT_LEARNING = {
  weeklyGoalChallenges: 5,
  reminderTime: '09:00',
  studyDays: ALL_WEEK_DAYS as number[],
};

// Bloque de accesibilidad oculto temporalmente; el estado y el guardado
// se mantienen para poder reactivarlo flippeando este flag sin tener que
// reconstruir nada.
const SHOW_ACCESSIBILITY_SETTINGS = false;

// Devuelve la franja (morning/afternoon/evening) a la que pertenece una
// hora "HH:MM". Mismo criterio que [[study-time-selector]] usa en el
// onboarding — ambos consumen `STUDY_TIME_HOURS` como fuente única.
function findBandForHour(hour: string | undefined): StudyTimeBand | null {
  if (!hour) return null;
  for (const band of STUDY_TIME_BANDS) {
    if (STUDY_TIME_HOURS[band].includes(hour)) return band;
  }
  return null;
}

const DEFAULT_PRIVACY = {
  showInRanking: true,
};

const DEFAULT_ACCESSIBILITY = {
  reducedMotion: false,
  highContrast: false,
  largerText: false,
  keyboardNavigation: true,
};

// Icono de información que abre un popover al pulsar (desktop y mobile).
// Usamos Radix Popover en lugar del atom Tooltip porque Tooltip solo
// responde a hover/focus — en mobile el tap no lo abre. Popover sí
// responde a click/tap, se cierra al pulsar fuera o con Esc, y soporta
// portal para no quedarse cortado por overflow del contenedor.
function InfoPopover({ content, ariaLabel }: { content: ReactNode; ariaLabel: string }) {
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={styles.settings__infoBtn}
          aria-label={ariaLabel}
        >
          <Info size={16} aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          sideOffset={8}
          collisionPadding={16}
          className={styles.settings__infoPopover}
        >
          {content}
          <PopoverPrimitive.Arrow className={styles.settings__infoPopoverArrow} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const push = usePushNotifications();
  const { toast } = useToast();
  const navGuard = useProfileNavGuard();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const proceedLeaveRef = useRef<(() => void) | null>(null);

  const categoriesQuery = useAsyncData<Category[]>({ fetchFn: () => categoriesAPI.list() });
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);

  // Idioma seleccionado (pendiente): NO se aplica al instante, solo al guardar.
  const [selectedLocale, setSelectedLocale] = useState<StudentLocale>(() => getStoredStudentLocale());
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATIONS);
  const [learningSettings, setLearningSettings] = useState(DEFAULT_LEARNING);
  // Franja del día seleccionada en el dropdown de "Hora del recordatorio".
  // Se inicializa a partir de la hora guardada en `reminderTime` y se
  // sincroniza vía effect cuando esta se actualiza (carga inicial o cambio
  // desde otra parte). Cambiar la franja no toca `reminderTime` por sí
  // solo — solo refresca el set de chips visibles para que el alumno
  // elija la nueva hora.
  const [selectedBand, setSelectedBand] = useState<StudyTimeBand>(
    () => findBandForHour(DEFAULT_LEARNING.reminderTime) ?? 'morning'
  );
  useEffect(() => {
    const band = findBandForHour(learningSettings.reminderTime);
    if (band) setSelectedBand(band);
  }, [learningSettings.reminderTime]);
  const [privacySettings, setPrivacySettings] = useState(DEFAULT_PRIVACY);
  const [accessibilitySettings, setAccessibilitySettings] = useState(DEFAULT_ACCESSIBILITY);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (location.hash !== '#settings-interface' || isLoading) return;
    const el = document.getElementById('settings-interface');
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash, isLoading]);

  const loadSettings = useCallback(async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      try {
        const [goal, notifPrefs, prefsRaw] = await Promise.all([
          getWeeklyGoal(user.id),
          getNotificationPreferences(user.id),
          ApiClient.get<Record<string, unknown>>('/users/me/preferences').catch((): Record<string, unknown> => ({})),
        ]);
        const prefs = prefsRaw;
        setLearningSettings((prev) => ({
          ...prev,
          weeklyGoalChallenges: goal.targetStories,
          reminderTime: (prefs['reminderTime'] as string) ?? prev.reminderTime,
          studyDays:
            Array.isArray(prefs['studyDays']) && (prefs['studyDays'] as number[]).length > 0
              ? (prefs['studyDays'] as number[])
              : ALL_WEEK_DAYS,
        }));
        setNotificationSettings({
          emailNotifications: notifPrefs.email,
          pushNotifications: notifPrefs.push,
          storyReminders: notifPrefs.storyReminders,
          achievementAlerts: notifPrefs.achievementAlerts,
          streakReminders: notifPrefs.streakReminders,
          dailyGoalReminders: (prefs['dailyGoalReminders'] as boolean) ?? true,
        });
        setPrivacySettings({
          showInRanking: (prefs['showInRanking'] as boolean) ?? DEFAULT_PRIVACY.showInRanking,
        });
        setAccessibilitySettings({
          reducedMotion: (prefs['reducedMotion'] as boolean) ?? DEFAULT_ACCESSIBILITY.reducedMotion,
          highContrast: (prefs['highContrast'] as boolean) ?? DEFAULT_ACCESSIBILITY.highContrast,
          largerText: (prefs['largerText'] as boolean) ?? DEFAULT_ACCESSIBILITY.largerText,
          keyboardNavigation: (prefs['keyboardNavigation'] as boolean) ?? DEFAULT_ACCESSIBILITY.keyboardNavigation,
        });
        const lp = prefs['learningPreferences'] as Partial<LearningPreferences> | undefined;
        setSelectedSlugs(Array.isArray(lp?.categorySlugs) ? lp!.categorySlugs! : []);
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
  }, [user?.id]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  // Recarga al cerrarse la modal de onboarding: el onboarding de personalización
  // pudo cambiar intereses / meta / hora de estudio mientras Ajustes seguía montada.
  useEffect(() => {
    const handler = () => void loadSettings();
    window.addEventListener(ONBOARDING_CLOSED_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_CLOSED_EVENT, handler);
  }, [loadSettings]);

  // Snapshot de los ajustes que controla el botón "Guardar" (meta, hora,
  // notificaciones, privacidad, accesibilidad e idioma). Las categorías se
  // editan aparte y el push es acción de dispositivo: quedan fuera.
  const settingsSnapshot = JSON.stringify({
    weeklyGoalChallenges: learningSettings.weeklyGoalChallenges,
    reminderTime: learningSettings.reminderTime,
    studyDays: learningSettings.studyDays,
    notif: notificationSettings,
    privacy: privacySettings,
    access: accessibilitySettings,
    locale: selectedLocale,
  });
  // Snapshot inicial tras cargar: marca el punto "sin cambios".
  const initialSnapshotRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isLoading && initialSnapshotRef.current === null) {
      initialSnapshotRef.current = settingsSnapshot;
    }
  }, [isLoading, settingsSnapshot]);
  const hasChanges =
    initialSnapshotRef.current !== null && settingsSnapshot !== initialSnapshotRef.current;

  const handleSaveSettings = async (): Promise<boolean> => {
    if (!user?.id) return false;
    setIsSaving(true);
    try {
      // Una sola petición atómica con TODO. Notificaciones, privacidad y
      // accesibilidad viven en el JSON `preferences`; `weeklyGoalTarget` va a su
      // propia columna pero el endpoint /me/preferences ya lo separa y lo
      // escribe en la misma actualización. Hacerlo en una sola llamada evita
      // que dos escrituras concurrentes sobre el mismo JSON se pisen.
      await ApiClient.patch('/users/me/preferences', {
        weeklyGoalTarget: learningSettings.weeklyGoalChallenges,
        // Notificaciones
        email: notificationSettings.emailNotifications,
        push: notificationSettings.pushNotifications,
        storyReminders: notificationSettings.storyReminders,
        achievementAlerts: notificationSettings.achievementAlerts,
        streakReminders: notificationSettings.streakReminders,
        // Resto de preferencias
        dailyGoalReminders: notificationSettings.dailyGoalReminders,
        reminderTime: learningSettings.reminderTime,
        studyDays: learningSettings.studyDays,
        showInRanking: privacySettings.showInRanking,
        reducedMotion: accessibilitySettings.reducedMotion,
        highContrast: accessibilitySettings.highContrast,
        largerText: accessibilitySettings.largerText,
        keyboardNavigation: accessibilitySettings.keyboardNavigation,
      });
      // Idioma elegido: se aplica solo ahora (al guardar), no al togglear.
      void studentI18n.changeLanguage(selectedLocale);
      setStoredStudentLocale(selectedLocale);
      setStudentDocumentLang(selectedLocale);
      // Marca el estado actual como guardado → el botón se deshabilita.
      initialSnapshotRef.current = settingsSnapshot;
      return true;
    } catch (error) {
      console.error('Failed to save settings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };


  // Guard de cambios sin guardar para el botón "volver" del wrapper (igual que Perfil).
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  useEffect(() => {
    navGuard.setGuard({
      isDirty: () => hasChangesRef.current,
      confirmLeave: (proceed) => {
        proceedLeaveRef.current = proceed;
        setLeaveOpen(true);
      },
    });
    return () => navGuard.setGuard(null);
  }, [navGuard]);

  const handleDiscardLeave = () => {
    setLeaveOpen(false);
    const proceed = proceedLeaveRef.current;
    proceedLeaveRef.current = null;
    proceed?.();
  };

  const handleSaveAndLeave = async () => {
    const ok = await handleSaveSettings();
    setLeaveOpen(false);
    if (ok) {
      const proceed = proceedLeaveRef.current;
      proceedLeaveRef.current = null;
      proceed?.();
    }
  };

  // Borrado permanente de la cuenta (requisito de las stores). Tras el 204 del
  // backend, cerramos sesión y volvemos al login. El diálogo previo actúa de
  // confirmación para evitar borrados accidentales.
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await ApiClient.delete('/users/me');
      setDeleteOpen(false);
      logout();
      navigate(ROUTES.AUTH.ACCOUNT_DELETED, { replace: true });
    } catch (error) {
      console.error('Failed to delete account:', error);
      setIsDeleting(false);
      toast({ description: t('settings.deleteAccount.error') });
    }
  };

  // Confirmación por escritura: el botón de borrado solo se habilita si el
  // usuario teclea la palabra requerida (insensible a mayúsculas/espacios).
  const deleteConfirmWord = t('settings.deleteAccount.confirmWord');
  const canConfirmDelete =
    deleteConfirmText.trim().toLowerCase() === deleteConfirmWord.toLowerCase();

  const closeDeleteDialog = (open: boolean) => {
    if (isDeleting) return;
    setDeleteOpen(open);
    if (!open) setDeleteConfirmText('');
  };

  if (isLoading) {
    return (
      <div className={styles.settings}>
        <div className={styles.settings__loading}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.settings__header}>
        <div>
          <h1 className={styles.settings__title}>{t('settings.title')}</h1>
          <p className={styles.settings__subtitle}>{t('settings.subtitle')}</p>
        </div>
      </div>

      <div className={styles.settings__content}>
        <Card id="settings-interface">
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Globe className={styles.settings__cardIcon} />
              <CardTitle className={styles.settings__cardTitle}>{t('settings.interface.cardTitle')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.interface.label')}</span>
                </div>
                <Switch
                  checked={selectedLocale === 'en'}
                  onCheckedChange={(checked) => setSelectedLocale(checked ? 'en' : 'es')}
                  aria-label={t('settings.interface.ariaLabel')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Bell className={styles.settings__cardIcon} />
              <CardTitle className={styles.settings__cardTitle}>{t('settings.notifications.cardTitle')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.notifications.emailLabel')}</span>
                </div>
                <Switch
                  checked={notificationSettings.emailNotifications}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, emailNotifications: checked })
                  }
                />
              </div>

              <div className={`${styles.settings__item} ${styles['settings__item--noBorder']}`}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.notifications.pushLabel')}</span>
                </div>
                <Switch
                  checked={push.isSubscribed}
                  disabled={push.permission === 'unsupported' || push.permission === 'denied'}
                  onCheckedChange={async (checked) => {
                    const ok = checked ? await push.subscribe() : await push.unsubscribe();
                    if (!ok) {
                      toast({
                        description: checked
                          ? t('settings.notifications.pushEnableError')
                          : t('settings.notifications.pushDisableError'),
                      });
                    }
                  }}
                />
              </div>

              {/*
                Toggles granulares de push: solo se muestran cuando el
                dispositivo tiene push activado (`isSubscribed`). Si el
                alumno tiene push global OFF, el backend ya descarta todos
                los envíos en `dispatchPush` por el opt-out global, así
                que estos toggles serían inertes y solo añaden ruido.
                El state se mantiene en memoria y el save sigue mandando
                sus valores actuales, así que al reactivar push las
                preferencias previas se conservan tal cual.
              */}
              {push.isSubscribed && (
                <>
                  <div className={`${styles.settings__item} ${styles['settings__item--nested']}`}>
                    <div className={styles.settings__itemInfo}>
                      <div className={styles.settings__itemLabelRow}>
                        <span className={styles.settings__itemLabel}>
                          {t('settings.notifications.storyRemindersLabel')}
                        </span>
                        <InfoPopover
                          content={t('settings.notifications.storyRemindersDesc')}
                          ariaLabel={t('settings.notifications.infoAriaLabel')}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.storyReminders}
                      onCheckedChange={(checked) =>
                        setNotificationSettings({ ...notificationSettings, storyReminders: checked })
                      }
                    />
                  </div>

                  <div className={`${styles.settings__item} ${styles['settings__item--nested']}`}>
                    <div className={styles.settings__itemInfo}>
                      <div className={styles.settings__itemLabelRow}>
                        <span className={styles.settings__itemLabel}>{t('settings.notifications.achievementLabel')}</span>
                        <InfoPopover
                          content={t('settings.notifications.achievementDesc')}
                          ariaLabel={t('settings.notifications.infoAriaLabel')}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.achievementAlerts}
                      onCheckedChange={(checked) =>
                        setNotificationSettings({ ...notificationSettings, achievementAlerts: checked })
                      }
                    />
                  </div>

                  <div className={`${styles.settings__item} ${styles['settings__item--nested']}`}>
                    <div className={styles.settings__itemInfo}>
                      <div className={styles.settings__itemLabelRow}>
                        <span className={styles.settings__itemLabel}>{t('settings.notifications.streakLabel')}</span>
                        <InfoPopover
                          content={t('settings.notifications.streakDesc')}
                          ariaLabel={t('settings.notifications.infoAriaLabel')}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.streakReminders}
                      onCheckedChange={(checked) =>
                        setNotificationSettings({ ...notificationSettings, streakReminders: checked })
                      }
                    />
                  </div>

                  <div className={`${styles.settings__item} ${styles['settings__item--nested']}`}>
                    <div className={styles.settings__itemInfo}>
                      <div className={styles.settings__itemLabelRow}>
                        <span className={styles.settings__itemLabel}>{t('settings.notifications.dailyGoalLabel')}</span>
                        <InfoPopover
                          content={t('settings.notifications.dailyGoalDesc')}
                          ariaLabel={t('settings.notifications.infoAriaLabel')}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.dailyGoalReminders}
                      onCheckedChange={(checked) =>
                        setNotificationSettings({ ...notificationSettings, dailyGoalReminders: checked })
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <BookOpen className={styles.settings__cardIcon} />
              <CardTitle className={styles.settings__cardTitle}>{t('settings.learning.cardTitle')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={`${styles.settings__item} ${styles['settings__item--stacked']}`}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.learning.studyDaysLabel')}</span>
                  <span className={styles.settings__itemDescription}>
                    {t('settings.learning.studyDaysDesc')}
                  </span>
                </div>
                <StudyDaysSelector
                  value={learningSettings.studyDays}
                  onChange={(days) =>
                    setLearningSettings({ ...learningSettings, studyDays: days })
                  }
                  hideHelper
                />
              </div>

              <div className={`${styles.settings__item} ${styles['settings__item--stacked']}`}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.learning.reminderTimeLabel')}</span>
                  <span className={styles.settings__itemDescription}>
                    {t('settings.learning.reminderTimeDesc')}
                  </span>
                </div>
                <Select
                  value={selectedBand}
                  onValueChange={(value) => setSelectedBand(value as StudyTimeBand)}
                  options={STUDY_TIME_BANDS.map((band) => ({
                    value: band,
                    label: t(`studyTimeSelector.bands.${band}`),
                  }))}
                />
                <ChipGroup
                  selectionMode="single"
                  value={learningSettings.reminderTime}
                  onValueChange={(value) =>
                    value &&
                    setLearningSettings({ ...learningSettings, reminderTime: value })
                  }
                  aria-label={t('settings.learning.reminderTimeLabel')}
                  className={styles.settings__reminderHours}
                >
                  {STUDY_TIME_HOURS[selectedBand].map((hour) => (
                    <Chip key={hour} value={hour}>
                      {hour}
                    </Chip>
                  ))}
                </ChipGroup>
              </div>

              <div className={`${styles.settings__item} ${styles['settings__item--stacked']}`}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.learning.weeklyGoalLabel')}</span>
                  <span className={styles.settings__itemDescription}>
                    {t('settings.learning.weeklyGoalDesc')}
                  </span>
                </div>
                <ChipGroup
                  selectionMode="single"
                  value={learningSettings.weeklyGoalChallenges.toString()}
                  onValueChange={(value) =>
                    value &&
                    setLearningSettings({
                      ...learningSettings,
                      weeklyGoalChallenges: parseInt(value, 10),
                    })
                  }
                  aria-label={t('settings.learning.weeklyGoalLabel')}
                >
                  {WEEKLY_ROUTINE_OPTIONS.map((count) => (
                    <Chip key={count} value={String(count)}>
                      {t(`settings.options.stories${count}`)}
                    </Chip>
                  ))}
                </ChipGroup>
              </div>

              <div className={`${styles.settings__item} ${styles['settings__item--stacked']}`}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.interests.cardTitle')}</span>
                  <span className={styles.settings__itemDescription}>
                    {t('settings.interests.description')}
                  </span>
                </div>
                {selectedSlugs.length > 0 && (
                  <div className={styles.settings__interests}>
                    {selectedSlugs
                      .map((slug) => categoriesQuery.data?.find((c) => c.slug === slug))
                      .filter((c): c is Category => !!c)
                      .map((cat) => (
                        <button
                          key={cat.slug}
                          type="button"
                          className={styles.settings__interestChip}
                          data-no-press-scale=""
                          onClick={() =>
                            navigate(`${ROUTES.STUDENT.ONBOARDING_PREFERENCES}?mode=edit`)
                          }
                        >
                          {localizedField(cat, 'name', i18n.language)}
                        </button>
                      ))}
                  </div>
                )}
                <div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(`${ROUTES.STUDENT.ONBOARDING_PREFERENCES}?mode=edit`)
                    }
                  >
                    {t('settings.interests.edit')}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Eye className={styles.settings__cardIcon} />
              <CardTitle className={styles.settings__cardTitle}>{t('settings.privacy.cardTitle')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>{t('settings.privacy.rankingLabel')}</span>
                </div>
                <Switch
                  checked={privacySettings.showInRanking}
                  onCheckedChange={(checked) =>
                    setPrivacySettings({ ...privacySettings, showInRanking: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <AlertTriangle className={styles.settings__cardIcon} />
              <CardTitle className={styles.settings__cardTitle}>
                {t('settings.dangerZone.cardTitle')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div
                className={`${styles.settings__item} ${styles['settings__item--stacked']} ${styles.settings__dangerItem}`}
              >
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>
                    {t('settings.deleteAccount.button')}
                  </span>
                  <span className={styles.settings__itemDescription}>
                    {t('settings.dangerZone.description')}
                  </span>
                </div>
                <Button
                  variant="destructive"
                  className={styles.settings__dangerButton}
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('settings.deleteAccount.button')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {SHOW_ACCESSIBILITY_SETTINGS && (
          <Card>
            <CardHeader>
              <div className={styles.settings__cardHeader}>
                <Zap className={styles.settings__cardIcon} />
                <CardTitle className={styles.settings__cardTitle}>{t('settings.accessibility.cardTitle')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className={styles.settings__section}>
                <div className={styles.settings__item}>
                  <div className={styles.settings__itemInfo}>
                    <span className={styles.settings__itemLabel}>
                      {t('settings.accessibility.reducedMotionLabel')}
                    </span>
                    <span className={styles.settings__itemDescription}>
                      {t('settings.accessibility.reducedMotionDesc')}
                    </span>
                  </div>
                  <Switch
                    checked={accessibilitySettings.reducedMotion}
                    onCheckedChange={(checked) =>
                      setAccessibilitySettings({ ...accessibilitySettings, reducedMotion: checked })
                    }
                  />
                </div>

                <div className={styles.settings__item}>
                  <div className={styles.settings__itemInfo}>
                    <span className={styles.settings__itemLabel}>{t('settings.accessibility.highContrastLabel')}</span>
                    <span className={styles.settings__itemDescription}>
                      {t('settings.accessibility.highContrastDesc')}
                    </span>
                  </div>
                  <Switch
                    checked={accessibilitySettings.highContrast}
                    onCheckedChange={(checked) =>
                      setAccessibilitySettings({ ...accessibilitySettings, highContrast: checked })
                    }
                  />
                </div>

                <div className={styles.settings__item}>
                  <div className={styles.settings__itemInfo}>
                    <span className={styles.settings__itemLabel}>{t('settings.accessibility.largerTextLabel')}</span>
                    <span className={styles.settings__itemDescription}>
                      {t('settings.accessibility.largerTextDesc')}
                    </span>
                  </div>
                  <Switch
                    checked={accessibilitySettings.largerText}
                    onCheckedChange={(checked) =>
                      setAccessibilitySettings({ ...accessibilitySettings, largerText: checked })
                    }
                  />
                </div>

                <div className={styles.settings__item}>
                  <div className={styles.settings__itemInfo}>
                    <span className={styles.settings__itemLabel}>{t('settings.accessibility.keyboardLabel')}</span>
                    <span className={styles.settings__itemDescription}>
                      {t('settings.accessibility.keyboardDesc')}
                    </span>
                  </div>
                  <Switch
                    checked={accessibilitySettings.keyboardNavigation}
                    onCheckedChange={(checked) =>
                      setAccessibilitySettings({ ...accessibilitySettings, keyboardNavigation: checked })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Guardar cambios: antes barra inferior fija; ahora al final del contenido
            (la barra de abajo es la navegación). */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!hasChanges || isSaving}
          isLoading={isSaving}
          onClick={() => void handleSaveSettings()}
        >
          {t('settings.saveChanges')}
        </Button>

      </div>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent hideDefaultClose>
          <IconButton
            icon={X}
            aria-label={t('settings.leaveGuard.close')}
            onClick={() => setLeaveOpen(false)}
            className={styles.settings__modalClose}
          />
          <DialogHeader>
            <DialogTitle>{t('settings.leaveGuard.title')}</DialogTitle>
            <DialogDescription>{t('settings.leaveGuard.message')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={handleDiscardLeave}>
              {t('settings.leaveGuard.discard')}
            </Button>
            <Button variant="primary" isLoading={isSaving} onClick={() => void handleSaveAndLeave()}>
              {t('settings.leaveGuard.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Drawer open={deleteOpen} onOpenChange={closeDeleteDialog}>
        <DrawerContent
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onSwipeToClose={() => closeDeleteDialog(false)}
          onEscapeKeyDown={() => closeDeleteDialog(false)}
          onPointerDownOutside={() => closeDeleteDialog(false)}
          closeSrLabel={t('settings.deleteAccount.cancel')}
        >
          <DrawerHeader className={styles.settings__deleteHeader}>
            <div className={styles.settings__deleteIcon}>
              <AlertTriangle size={24} />
            </div>
            <DrawerTitle className="sr-only">{t('settings.deleteAccount.title')}</DrawerTitle>
            <DrawerDescription className={styles.settings__deleteDescription}>
              {t('settings.deleteAccount.description', { word: deleteConfirmWord })}
            </DrawerDescription>
          </DrawerHeader>
          <div className={styles.settings__deleteConfirm}>
            <Input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteConfirmWord}
              aria-label={t('settings.deleteAccount.confirmPrompt', { word: deleteConfirmWord })}
              disabled={isDeleting}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              fullWidth
            />
          </div>
          <DrawerFooter className={styles.settings__deleteFooter}>
            <Button variant="ghost" disabled={isDeleting} onClick={() => closeDeleteDialog(false)}>
              {t('settings.deleteAccount.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!canConfirmDelete || isDeleting}
              isLoading={isDeleting}
              onClick={() => void handleDeleteAccount()}
            >
              {t('settings.deleteAccount.confirm')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export default SettingsPage;
